"use client";

import { useState } from "react";
import CategoryVoucherCard from "./CategoryVoucherCard";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import { getTranslations } from "@/lib/i18n";
import { useAvailableFixedDateCampaignCollections } from "@/lib/use-fixed-date-voucher-unavailable";

const DEFAULT_VISIBLE = 6;

// 2026-08-30 above-the-fold fix (Dennis, regie-sessie, AskUserQuestion-keuze
// -- citeerbare bron: bezoekers zagen op elke categoriepagina maar 1 deal
// boven de vouw omdat de gele band bij 1-2 vouchers een 3-koloms grid met
// (een) reusachtige kaart(en) rendert). At/below this count the band
// switches to a compact full-width row per voucher; at/above it the band
// is completely unchanged (grid + DEFAULT_VISIBLE + expand button).
const COMPACT_ROW_THRESHOLD = 2;

export default function CategoryVoucherStrip({ vouchers, categoryDisplayName }) {
  const { locale } = useSiteContext();
  const { t } = getTranslations(locale);
  const [expanded, setExpanded] = useState(false);
  const [availableVouchers] =
    useAvailableFixedDateCampaignCollections(vouchers);

  if (availableVouchers.length === 0) return null;

  const heading = t("listing.vouchers_in_category", "Vouchers in {name}", {
    name: categoryDisplayName,
  });

  if (availableVouchers.length <= COMPACT_ROW_THRESHOLD) {
    return (
      <section
        aria-labelledby="category-voucher-strip-heading"
        className="mb-8 rounded-xl p-3 lg:p-4"
        style={{ background: "linear-gradient(180deg, #fff8ec 0%, #fffdf7 100%)", border: "1px solid #f5e1b8" }}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 id="category-voucher-strip-heading" className="text-lg lg:text-xl font-semibold text-gray-900 flex items-center gap-2">
            <span aria-hidden="true">🎁</span>
            {heading}
            <span className="text-sm font-normal text-gray-500">({availableVouchers.length})</span>
          </h2>
        </div>

        <div className="flex flex-col gap-2">
          {availableVouchers.map((voucher, idx) => (
            <CategoryVoucherCard
              key={voucher.deals_id || voucher.id || idx}
              voucher={voucher}
              position={idx}
              variant="compact"
            />
          ))}
        </div>
      </section>
    );
  }

  const visible = expanded
    ? availableVouchers
    : availableVouchers.slice(0, DEFAULT_VISIBLE);
  const hasMore = availableVouchers.length > DEFAULT_VISIBLE;

  return (
    <section
      aria-labelledby="category-voucher-strip-heading"
      className="mb-8 rounded-xl p-4 lg:p-5"
      style={{ background: "linear-gradient(180deg, #fff8ec 0%, #fffdf7 100%)", border: "1px solid #f5e1b8" }}
    >
      <div className="flex items-center justify-between mb-3">
        <h2 id="category-voucher-strip-heading" className="text-lg lg:text-xl font-semibold text-gray-900 flex items-center gap-2">
          <span aria-hidden="true">🎁</span>
          {heading}
          <span className="text-sm font-normal text-gray-500">({availableVouchers.length})</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
        {visible.map((voucher, idx) => (
          <CategoryVoucherCard
            key={voucher.deals_id || voucher.id || idx}
            voucher={voucher}
            position={idx}
          />
        ))}
      </div>

      {hasMore && !expanded && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-sm font-medium text-blue-700 hover:text-blue-800 hover:underline"
          >
            {t("listing.show_more_vouchers", "Toon alle {count} vouchers", { count: availableVouchers.length })}
          </button>
        </div>
      )}
    </section>
  );
}
